/**
 * Query keys and hooks.
 *
 * Keys live in one place so that the realtime layer and the components cannot
 * disagree about what to invalidate — a mismatch there shows up as a screen
 * that quietly stops updating, which is hard to notice and harder to debug.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AddSlotsBody, FulfillmentState } from "@soi/contracts";
import { api } from "./api.js";

export const queryKeys = {
  me: ["me"] as const,
  sites: ["sites"] as const,
  zones: ["zones"] as const,
  site: (slug: string) => ["site", slug] as const,
  myDeliveries: ["my-deliveries"] as const,
  stock: ["stock"] as const,
  dashboard: ["dashboard"] as const,
  intervenants: ["intervenants"] as const,
  users: ["users"] as const,
  adminSites: ["admin-sites"] as const,
  adminReservations: (slug: string) => ["admin-reservations", slug] as const,
  adminSlotDays: ["admin-slot-days"] as const,
  adminDaySlots: (day: string) => ["admin-day-slots", day] as const,
};

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: api.me,
    // A 401 is the normal answer for a visitor, not a failure worth retrying.
    retry: false,
    staleTime: 60_000,
  });
}

export const useSites = () => useQuery({ queryKey: queryKeys.sites, queryFn: api.sites });
export const useZones = () =>
  useQuery({ queryKey: queryKeys.zones, queryFn: api.zones, staleTime: Infinity });

export const useSite = (slug: string | null) =>
  useQuery({
    queryKey: queryKeys.site(slug ?? ""),
    queryFn: () => api.site(slug!),
    enabled: Boolean(slug),
  });

export const useMyDeliveries = () =>
  useQuery({ queryKey: queryKeys.myDeliveries, queryFn: api.myDeliveries });

export const useStock = () => useQuery({ queryKey: queryKeys.stock, queryFn: api.stock });
export const useDashboard = () =>
  useQuery({ queryKey: queryKeys.dashboard, queryFn: api.dashboard });
export const useIntervenants = () =>
  useQuery({ queryKey: queryKeys.intervenants, queryFn: api.intervenants });
export const useUsers = () => useQuery({ queryKey: queryKeys.users, queryFn: api.users });

export function useToggleDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slotId, delivered }: { slotId: string; delivered: boolean }) =>
      delivered ? api.cancelDelivery(slotId) : api.validateDelivery(slotId),
    // No optimistic update: the server's answer is the one that counts, and a
    // slot wrongly shown as delivered would misreport the deployment.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.myDeliveries });
      void qc.invalidateQueries({ queryKey: queryKeys.stock });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export const useAdminSites = () =>
  useQuery({ queryKey: queryKeys.adminSites, queryFn: api.adminReservationSites });

export const useAdminReservations = (slug: string | null) =>
  useQuery({
    queryKey: queryKeys.adminReservations(slug ?? ""),
    queryFn: () => api.adminReservations(slug!),
    enabled: slug !== null,
  });

/**
 * Everything a change of progress touches, invalidated together.
 *
 * Reaching "terminée" writes a delivery row, so the indicators and the stock
 * move with it. Forgetting either would leave an operator staring at a KPI
 * that only catches up on the next reload — the very thing the shared database
 * was meant to end.
 */
function invalidateFulfillment(qc: ReturnType<typeof useQueryClient>, slug: string) {
  void qc.invalidateQueries({ queryKey: queryKeys.adminReservations(slug) });
  void qc.invalidateQueries({ queryKey: queryKeys.adminSites });
  void qc.invalidateQueries({ queryKey: queryKeys.dashboard });
  void qc.invalidateQueries({ queryKey: queryKeys.stock });
  void qc.invalidateQueries({ queryKey: queryKeys.site(slug) });
}

export function useSetFulfillment(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fulfillment }: { id: string; fulfillment: FulfillmentState }) =>
      api.adminSetFulfillment(id, { fulfillment }),
    // No optimistic update, for the same reason as deliveries: a state shown
    // as "terminée" that the server refused would misreport the deployment.
    onSuccess: () => invalidateFulfillment(qc, slug),
  });
}

export function useAdminCancelReservation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.adminCancelReservation(id),
    onSuccess: () => invalidateFulfillment(qc, slug),
  });
}

/** Nothing to invalidate: a reminder changes no booking, it only sends a mail. */
export function useAdminSendReminder() {
  return useMutation({
    mutationFn: (id: string) => api.adminSendReminder(id),
  });
}

export function useAdjustStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, delta }: { slug: string; delta: number }) =>
      api.adjustStock(slug, { delta }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.stock });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export const useAdminSlotDays = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.adminSlotDays,
    queryFn: api.adminSlotDays,
    enabled,
  });

/** What is already planned on the chosen day, to warn before submitting. */
export const useAdminDaySlots = (day: string) =>
  useQuery({
    queryKey: queryKeys.adminDaySlots(day),
    queryFn: () => api.adminDaySlots(day),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(day),
  });

export function useAddSlots() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddSlotsBody) => api.addSlots(body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: queryKeys.sites });
      for (const siteSlug of new Set(result.entries.map((e) => e.siteSlug))) {
        void qc.invalidateQueries({ queryKey: queryKeys.site(siteSlug) });
      }
      void qc.invalidateQueries({ queryKey: queryKeys.adminSites });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard });
      void qc.invalidateQueries({ queryKey: queryKeys.adminSlotDays });
      void qc.invalidateQueries({ queryKey: ["admin-day-slots"] });
    },
  });
}

export function usePatchStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      slug,
      realSeats,
      version,
    }: {
      slug: string;
      realSeats: number | null;
      version: number;
    }) => api.patchStock(slug, { realSeats, version }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.stock });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}
